<div class="detail-page">
  <div class="detail-actions">
    <a routerLink="/status" class="back-link">
      <mat-icon>arrow_back</mat-icon>
      Back to overview
    </a>
    @if (data()) {
      <button mat-stroked-button (click)="downloadTransaction()" class="download-button"
              matTooltip="Save the full transaction payload (timings, byStage, logs, errors) as a JSON file for debugging or sharing.">
        <mat-icon>download</mat-icon>
        Download JSON
      </button>
    }
  </div>

  @if (loading()) {
    <div class="status-loading">Loading transaction…</div>
  } @else if (error()) {
    <div class="status-error">
      <mat-icon>error_outline</mat-icon>
      <span>{{ error() }}</span>
    </div>
  } @else if (data(); as vm) {
    <header class="detail-header">
      <div class="detail-title">
        <h1>{{ title() }}</h1>
        @if (vm.phases?.aiAnalysis?.outcome === 'in-progress') {
          <span class="status-badge in-progress" matTooltip="The AI analysis is still running. This page auto-refreshes every 5 seconds.">
            <mat-icon>autorenew</mat-icon>
            in progress
          </span>
        } @else {
          <span class="status-badge"
                [class.ok]="vm.outcome === 'success'"
                [class.bad]="vm.outcome !== 'success'">
            <mat-icon>{{ vm.outcome === 'success' ? 'check_circle' : 'warning' }}</mat-icon>
            {{ vm.outcome === 'success' ? 'success' : 'failed' }}
          </span>
        }
        @if (vm.attempt > 0) {
          <span class="attempt-pill"
                matTooltip="Earlier attempts hit retryable errors and were retried automatically.">
            attempt #{{ vm.attempt + 1 }}
          </span>
        }
      </div>
      <div class="detail-meta">
        @if (vm.queuedAt) { Queued {{ vm.queuedAt | date:'medium' }} · }
        Started {{ vm.startedAt | date:'medium' }}
        @if (vm.completedAt) { · Completed {{ vm.completedAt | date:'medium' }} }
      </div>
    </header>

    @if (summary(); as s) {
      <section class="status-summary"
               [class.ok]="!s.inProgress && s.success"
               [class.bad]="!s.inProgress && !s.success"
               [class.in-progress]="s.inProgress">
        <div class="status-summary-icon">
          <mat-icon>{{ s.inProgress ? 'autorenew' : (s.success ? 'check_circle' : 'warning') }}</mat-icon>
        </div>
        <div class="status-summary-body">
          <h2 class="status-summary-headline">{{ s.headline }}</h2>
          <p class="status-summary-line">{{ s.durationLine }}</p>
          @if (s.slowestLine) {
            <p class="status-summary-line">{{ s.slowestLine }}</p>
          }
          @if (s.retryLine) {
            <p class="status-summary-line">{{ s.retryLine }}</p>
          }
          @if (s.errorLine) {
            <p class="status-summary-line bad-text">{{ s.errorLine }}</p>
          }
        </div>
      </section>
    }

    <div class="status-technical-divider">
      <span class="status-technical-label">Technical details</span>
      <span class="status-technical-hint">Step-by-step timings, network/I-O breakdown, and the raw log stream from this run.</span>
    </div>

    @if (vm.outcome === 'error' && vm.error) {
      <mat-card class="detail-card error-card">
        <mat-card-content>
          <h2>What went wrong</h2>
          <div class="error-summary">
            <mat-icon class="error-icon">error_outline</mat-icon>
            <div class="error-body">
              @if (vm.error.stage) {
                <div class="error-stage">Failed at step: <strong>{{ vm.error.stage }}</strong></div>
              }
              <div class="error-message">{{ vm.error.message || 'Unknown error' }}</div>
              <div class="error-meta">
                @if (vm.error.retryable) {
                  <span class="kv-hint">This was a transient error — the queue auto-retried; this is the final attempt.</span>
                } @else {
                  <span class="kv-hint">Permanent failure — no auto-retry was attempted.</span>
                }
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>
    }

    <div class="detail-grid">
      <mat-card class="detail-card">
        <mat-card-content>
          <h2>Summary</h2>
          <dl class="kv">
            <dt>Outcome</dt>
            <dd>{{ vm.outcome }}</dd>
            <dt>Total wall time</dt>
            <dd>{{ vm.runMs }} ms</dd>
            <dt>Queue wait</dt>
            <dd>{{ vm.queueWaitMs }} ms <span class="kv-hint">time between enqueue and worker pickup</span></dd>
            <dt>Attempt</dt>
            <dd>{{ vm.attempt + 1 }}</dd>
            <dt>Document</dt>
            <dd class="mono small">{{ vm.documentId }}</dd>
            <dt>Project</dt>
            <dd class="mono small">{{ vm.projectId }}</dd>
          </dl>
        </mat-card-content>
      </mat-card>

      <mat-card class="detail-card">
        <mat-card-content>
          <h2>Time breakdown</h2>
          <dl class="kv">
            <dt>Active CPU work</dt>
            <dd>
              {{ totals().cpu }} ms
              <span class="kv-hint">JSDOM, ProseMirror, JSZip — synchronous computation</span>
            </dd>
            <dt>Waiting (I/O, network)</dt>
            <dd>
              {{ totals().awaitMs }} ms
              <span class="kv-hint">OpenAI calls, S3 reads/writes</span>
            </dd>
            <dt>Event loop blocked</dt>
            <dd>
              {{ totals().blocked }} ms
              <span class="kv-hint">Total stalls &gt;50ms across the run — indicates heavy sync work</span>
            </dd>
          </dl>
        </mat-card-content>
      </mat-card>

      @if (vm.apiCalls) {
        <mat-card class="detail-card">
          <mat-card-content>
            <h2>OpenAI usage</h2>
            <dl class="kv">
              <dt>API calls</dt><dd>{{ vm.apiCalls.count ?? '—' }}</dd>
              <dt>Cache hits</dt>
              <dd>
                {{ vm.apiCalls.cacheHits ?? 0 }} / {{ (vm.apiCalls.cacheHits ?? 0) + (vm.apiCalls.cacheMisses ?? 0) }}
                <span class="kv-hint">prompt-response reuse from the local cache</span>
              </dd>
              <dt>Tokens (input)</dt>
              <dd>{{ vm.apiCalls.inputTokens ?? 0 }} <span class="kv-hint">({{ vm.apiCalls.cachedInputTokens ?? 0 }} cached)</span></dd>
              <dt>Tokens (output)</dt>
              <dd>{{ vm.apiCalls.outputTokens ?? 0 }}</dd>
              <dt>Estimated cost</dt>
              <dd>${{ (vm.apiCalls.estimatedCostUsd ?? 0).toFixed(4) }}</dd>
            </dl>
          </mat-card-content>
        </mat-card>
      }

      @if (vm.imageProcessing && vm.imageProcessing.total > 0) {
        <mat-card class="detail-card">
          <mat-card-content>
            <h2>Image processing</h2>
            <dl class="kv">
              <dt>Total</dt><dd>{{ vm.imageProcessing.total }}</dd>
              <dt>Processed</dt><dd>{{ vm.imageProcessing.processed }}</dd>
              <dt>Skipped</dt><dd>{{ vm.imageProcessing.skipped }}</dd>
              <dt>Failed</dt>
              <dd [class.bad-text]="vm.imageProcessing.failed > 0">{{ vm.imageProcessing.failed }}</dd>
            </dl>
            @if (vm.imageProcessing.errors && vm.imageProcessing.errors.length > 0) {
              <h3 class="sub">Per-image errors ({{ vm.imageProcessing.errors.length }})</h3>
              <ul class="error-list">
                @for (err of vm.imageProcessing.errors; track $index) {
                  <li>
                    @if (err.imageId) { <code>{{ err.imageId }}</code>: }
                    {{ err.error }}
                  </li>
                }
              </ul>
            }
          </mat-card-content>
        </mat-card>
      }

      @if (vm.logs && vm.logs.length > 0) {
        <mat-card class="detail-card span-2">
          <mat-card-content>
            <h2>Log stream</h2>
            <p class="hint">All log entries emitted while this transaction was processing, in order. Useful for spotting where a run started behaving unexpectedly.</p>
            <ul class="log-list">
              @for (entry of vm.logs; track $index) {
                <li class="log-entry">
                  @if (entry.timestamp) {
                    <span class="log-time">{{ entry.timestamp | date:'HH:mm:ss.SSS' }}</span>
                  }
                  @if (entry.step) {
                    <span class="log-step">{{ entry.step }}</span>
                  }
                  @if (entry.progress != null) {
                    <span class="log-progress">{{ entry.progress }}%</span>
                  }
                  <span class="log-message">{{ entry.message }}</span>
                </li>
              }
            </ul>
          </mat-card-content>
        </mat-card>
      }

      <mat-card class="detail-card span-2">
        <mat-card-content>
          <h2>Time per step</h2>
          <p class="hint">
            Each bar shows total wall time for one step, split into
            <strong style="color:#f97316">active CPU work</strong> and
            <strong style="color:#3b82f6">waiting (network/disk)</strong>.
            Long blue bars mean the step is bottlenecked on OpenAI or S3; long orange bars mean we're doing local compute.
          </p>
          @if (!vm.byStage.length) {
            <p class="empty">No step timings recorded.</p>
          } @else {
            <div class="stage-chart-wrap" [style.height.px]="chartHeightPx()">
              <canvas #breakdownChart></canvas>
            </div>
            <table class="status-table" style="margin-top: 24px;">
              <thead>
                <tr>
                  <th>Step</th>
                  <th class="num">Wall</th>
                  <th class="num">CPU</th>
                  <th class="num">Await</th>
                  <th class="num" matTooltip="Time the event loop was blocked (>50ms ticks) during this step">Blocked</th>
                  <th class="num" matTooltip="99th-percentile event-loop delay observed during this step">Loop p99</th>
                </tr>
              </thead>
              <tbody>
                @for (row of vm.byStage; track row.stage) {
                  <tr>
                    <td>{{ row.stage }}</td>
                    <td class="num">{{ row.wallMs }} ms</td>
                    <td class="num">{{ row.cpuMs ?? 0 }} ms</td>
                    <td class="num">{{ row.awaitMs ?? 0 }} ms</td>
                    <td class="num">{{ row.eventLoopBlockedMs ?? 0 }} ms</td>
                    <td class="num">{{ row.eventLoopP99Ms ?? 0 }} ms</td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </mat-card-content>
      </mat-card>
    </div>
  }
</div>
